import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, saveSession } from "@/lib/api";
import { Shield, KeyRound } from "lucide-react";

export default function AdminLogin() {
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // MFA step state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const finish = (data) => {
    saveSession(data.user.role || "admin", data);
    toast.success(`Welcome, ${data.user.name || "Admin"}`);
    nav("/admin/dashboard");
  };

  const submit = async () => {
    if (!identifier.trim() || !password) {
      toast.error("Enter your email or phone and password");
      return;
    }
    setLoading(true);
    const tryLogin = async (role) =>
      api.post(`/auth/login`, { phone: identifier.trim(), password, role });
    try {
      let data;
      try {
        ({ data } = await tryLogin("super_admin"));
      } catch (e) {
        if (e.response?.status === 401 || e.response?.status === 400) {
          ({ data } = await tryLogin("admin"));
        } else {
          throw e;
        }
      }
      if (data.mfa_required) {
        setMfaRequired(true);
        setMfaToken(data.mfa_token);
        toast.info("Enter the 6-digit code from Google Authenticator");
      } else {
        finish(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const submitMfa = async () => {
    if (!mfaCode.trim()) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/auth/mfa/verify`, { mfa_token: mfaToken, code: mfaCode.trim() });
      finish(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid authentication code");
    } finally { setLoading(false); }
  };

  return (
    <div className="admin-shell flex items-center justify-center p-6">
      <Card className="admin-card w-full max-w-md p-7">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 bg-orange-500 rounded-lg flex items-center justify-center">
            {mfaRequired ? <KeyRound className="text-white"/> : <Shield className="text-white"/>}
          </div>
          <div>
            <div className="font-display text-xl font-bold">KARMIVO Admin</div>
            <div className="text-xs text-slate-400">{mfaRequired ? "Two-Factor Authentication" : "Operations Control Center"}</div>
          </div>
        </div>

        {!mfaRequired ? (
          <div className="space-y-3">
            <div>
              <Label className="text-slate-300">Email or Phone</Label>
              <Input
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                placeholder="admin@karmivo.in or 98XXXXXXXX"
                className="bg-slate-900 border-slate-700"
                data-testid="admin-identifier-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && submit()}
                className="bg-slate-900 border-slate-700"
                data-testid="admin-password-input"
              />
            </div>
            <Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={submit} disabled={loading} data-testid="admin-login-btn">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
            <p className="text-xs text-slate-500 text-center">Access restricted to authorized Karmivo Admins only.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="mfa-step">
            <p className="text-sm text-slate-400">Enter the 6-digit code shown in Google Authenticator, or a recovery code.</p>
            <div>
              <Label className="text-slate-300">Authentication Code</Label>
              <Input
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
                onKeyDown={e => e.key === "Enter" && submitMfa()}
                placeholder="123456"
                className="bg-slate-900 border-slate-700 text-center font-mono text-xl tracking-[0.5em]"
                maxLength={14}
                autoFocus
                data-testid="admin-mfa-input"
              />
            </div>
            <Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={submitMfa} disabled={loading} data-testid="admin-mfa-verify-btn">
              {loading ? "Verifying…" : "Verify & Sign In"}
            </Button>
            <Button variant="ghost" size="sm" className="w-full text-slate-400" onClick={() => { setMfaRequired(false); setMfaToken(""); setMfaCode(""); }} data-testid="admin-mfa-back">
              Back to login
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
